/* =====================================================================================
   The data layer.

   Every module reads and writes through this file. Today it is backed by the seed data
   compiled into the bundle; the moment a Supabase project exists it is backed by Postgres,
   and NOT ONE COMPONENT CHANGES. That is the whole point of it existing.

   The switch is a single environment variable. If VITE_SUPABASE_URL is set the Supabase
   adapter is used, otherwise the local one. There is no third state and no per-module
   conditional, because that is how data layers rot.
   ===================================================================================== */

import { type Site, type Pathway } from '../data/atlas';
import { TENANT } from '@tenant';
import { type Opportunity } from '../data/commercial.config';
import { type Asset } from '../data/studio';

export type Backend = 'local' | 'supabase';

/* A DEMONSTRATION TENANT CAN NEVER HAVE A BACKEND.
 *
 * Vite loads .env.local for every build, including a white-label demonstration build. That
 * put another tenant's Supabase URL and anon key into the demonstration bundle, pointed the
 * demo at a real customer's database, and — because BACKEND then read 'supabase' — silently
 * tree-shook away every piece of the demo tenant's own seed data as unreachable. The visible
 * symptom was an empty Crew module; the actual defect was a live credential for someone
 * else's data shipped inside a public demo.
 *
 * The tenant declaring itself a demonstration is therefore decisive, and is checked BEFORE
 * the environment. A stray environment variable can no longer connect a demo to anything. */
export const BACKEND: Backend =
  TENANT.demo ? 'local'
  : import.meta.env.VITE_SUPABASE_URL ? 'supabase' : 'local';

/* --- The object graph, keyed so the palette and search can iterate generically -------- */

export interface ObjectDef {
  key: string;
  label: string;
  plural: string;
  route: (id: string) => string;
  /** Postgres source, used by the Supabase adapter and as documentation either way. */
  table: string;
  titleOf: (row: any) => string;
  subtitleOf?: (row: any) => string;
}

export const OBJECTS: Record<string, ObjectDef> = {
  site: {
    key: 'site', label: 'Centre', plural: 'Centres', table: 'atlas.sites',
    route: (id) => `/atlas/site/${id}`,
    titleOf: (r: Site) => r.name,
    subtitleOf: (r: Site) => [r.city, r.region].filter(Boolean).join(' · '),
  },
  country: {
    key: 'country', label: 'Country', plural: 'Countries', table: 'atlas.countries',
    route: (id) => `/atlas/country/${id}`,
    titleOf: (r: any) => r.name,
    subtitleOf: (r: any) => r.registry ?? '',
  },
  pathway: {
    key: 'pathway', label: 'Reimbursement route', plural: 'Reimbursement routes',
    table: 'atlas.reimbursement_pathways',
    route: (id) => `/atlas/pathway/${id}`,
    titleOf: (r: Pathway) => r.routeName,
    subtitleOf: (r: Pathway) => [r.decisionBody, r.iso3].filter(Boolean).join(' · '),
  },
  opportunity: {
    key: 'opportunity', label: 'Opportunity', plural: 'Pipeline', table: 'pipeline.opportunities',
    route: (id) => `/pipeline/${id}`,
    titleOf: (r: Opportunity) => r.name,
    subtitleOf: (r: Opportunity) => r.buyingAccount,
  },
  partner: {
    key: 'partner', label: 'Partner', plural: 'Partners', table: 'channel.partners',
    route: (id) => `/channel/${id}`,
    titleOf: (r: any) => r.name,
    subtitleOf: (r: any) => `${r.country} · ${String(r.type).replace(/_/g, ' ')}`,
  },
  tender: {
    key: 'tender', label: 'Tender', plural: 'Tenders', table: 'channel.tenders',
    route: (id) => `/channel/tender/${id}`,
    titleOf: (r: any) => r.title,
    subtitleOf: (r: any) => r.authority,
  },
  asset: {
    key: 'asset', label: 'Asset', plural: 'Studio assets', table: 'studio.assets',
    route: (id) => `/studio/${id}`,
    titleOf: (r: Asset) => r.title,
    subtitleOf: (r: Asset) => r.jobCode,
  },
};

/* --- Local adapter ------------------------------------------------------------------- */

/* The Atlas is researched fact and stays. The four business objects are worked examples and
   are gated at source in data/commercial.ts and data/studio.ts, so a module importing them
   directly cannot bypass the gate either. With Supabase connected they arrive empty and each
   module shows its real empty state. */
/* The seed comes from the active tenant, resolved by vite.config.ts at build time. Forum is
   white-label: which market model the demonstration runs on is a build input, not a code
   branch, and exactly one tenant's data is bundled. */
const LOCAL: Record<string, any[]> = {
  site: TENANT.seed.site,
  country: TENANT.seed.country as any[],
  pathway: TENANT.seed.pathway,
  opportunity: TENANT.seed.opportunity as any[],
  partner: TENANT.seed.partner as any[],
  tender: TENANT.seed.tender as any[],
  asset: TENANT.seed.asset as any[],
};

/* Writes made in this session, layered over the seed so the interface behaves like a real
   system before Postgres exists. Persisted to localStorage so a refresh does not lose work,
   which matters because congress capture is the highest-value moment in the product. */
const OVERLAY_KEY = 'forum.overlay.v1';

type Overlay = Record<string, Record<string, any>>;

function loadOverlay(): Overlay {
  try {
    return JSON.parse(localStorage.getItem(OVERLAY_KEY) || '{}');
  } catch {
    return {};
  }
}
let overlay: Overlay = loadOverlay();

function saveOverlay() {
  try {
    localStorage.setItem(OVERLAY_KEY, JSON.stringify(overlay));
  } catch {
    /* quota or private mode: the queue below is the durable path */
  }
}

/* --- Change notification. Deliberately tiny: components subscribe, no state library. --- */

type Listener = () => void;
const listeners = new Set<Listener>();
export function subscribe(fn: Listener) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
/* Snapshot version. Bumped on every write; see list() for why this exists. */
let version = 0;
const snapshots = new Map<string, { v: number; rows: unknown[] }>();

function emit() {
  version += 1;          // invalidates every cached snapshot
  listeners.forEach((l) => l());
}

/* --- Offline write queue -------------------------------------------------------------
   Congress wi-fi is unreliable and the intelligence is worth most in the ninety seconds
   after the conversation. Writes are applied optimistically to the overlay and queued;
   the queue drains when a backend exists and connectivity returns. Nothing is ever lost
   because the user was in a basement in Milan. */

export interface QueuedWrite {
  id: string;
  at: string;
  op: 'insert' | 'update';
  object: string;
  recordId: string;
  payload: Record<string, any>;
  synced: boolean;
  error?: string;
}

const QUEUE_KEY = 'forum.queue.v1';

function loadQueue(): QueuedWrite[] {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
  } catch {
    return [];
  }
}
let queue: QueuedWrite[] = loadQueue();

function saveQueue() {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  } catch {
    /* nothing more we can do; the overlay still holds the value */
  }
}

export function pendingWrites(): QueuedWrite[] {
  return queue.filter((q) => !q.synced);
}

function enqueue(op: QueuedWrite['op'], object: string, recordId: string, payload: Record<string, any>) {
  queue.push({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    at: new Date().toISOString(),
    op, object, recordId, payload,
    synced: BACKEND === 'local' ? false : false,
  });
  saveQueue();
}

/** Drains the queue against the real backend. No-op until Supabase is configured. */
export async function syncQueue(): Promise<{ synced: number; failed: number }> {
  if (BACKEND === 'local' || !navigator.onLine) return { synced: 0, failed: 0 };
  let synced = 0, failed = 0;
  for (const q of queue.filter((x) => !x.synced)) {
    try {
      const { supabaseWrite } = await import('./supabase');
      await supabaseWrite(OBJECTS[q.object].table, q.op, q.recordId, q.payload);
      q.synced = true;
      synced++;
    } catch (e) {
      q.error = e instanceof Error ? e.message : String(e);
      failed++;
    }
  }
  saveQueue();
  emit();
  return { synced, failed };
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => { void syncQueue(); });

  /* AND ON LOAD, which is the case that actually stranded somebody.

     A queued write that fails stays in the queue with synced=false, which is correct: the
     record is not lost and the next drain will retry it. But the only trigger was the browser
     'online' event, and that never fires for a user who was online the whole time. So two
     field intelligence records sat in one person's browser for a day, visible to her because
     the overlay renders them, invisible to everyone else, and retried never.

     Draining once at startup costs nothing when the queue is empty and recovers the queue when
     it is not. Deferred past first paint so it never delays the interface. */
  const drain = () => { void syncQueue(); };
  if (document.readyState === 'complete') setTimeout(drain, 1200);
  else window.addEventListener('load', () => setTimeout(drain, 1200), { once: true });
}

/* --- Read API ------------------------------------------------------------------------- */

function withOverlay(object: string, rows: any[]): any[] {
  const ov = overlay[object];
  if (!ov) return rows;
  const byId = new Map(rows.map((r) => [String(r.id), r]));
  for (const [id, patch] of Object.entries(ov)) {
    const existing = byId.get(id);
    byId.set(id, existing ? { ...existing, ...patch } : patch);
  }
  return [...byId.values()];
}

/* Snapshot cache.
   useSyncExternalStore compares the value returned by getSnapshot BY REFERENCE. withOverlay
   builds a fresh array every call, so every render produced a "new" snapshot, React concluded
   the store had changed again, and re-rendered forever. The symptom is not a warning: the
   component tree unmounts and the page goes blank.

   Caching the array until something actually writes makes the reference stable. `version` is
   bumped by emit(), which is the one place a change can originate. */
export function list<T = any>(object: string): T[] {
  const hit = snapshots.get(object);
  if (hit && hit.v === version) return hit.rows as T[];
  const rows = withOverlay(object, LOCAL[object] ?? []);
  snapshots.set(object, { v: version, rows });
  return rows as T[];
}

export function get<T = any>(object: string, id: string): T | undefined {
  return list<any>(object).find((r) => String(r.id) === String(id));
}

/* --- Write API -----------------------------------------------------------------------
   Optimistic by construction: the overlay is updated first and the caller sees the change
   immediately, then the write is queued. The UI never waits for a round trip to show the
   user their own typing. */

export function update(object: string, id: string, patch: Record<string, any>) {
  overlay[object] = overlay[object] || {};
  overlay[object][id] = { ...(overlay[object][id] || {}), ...patch };
  saveOverlay();
  enqueue('update', object, id, patch);
  emit();
  void syncQueue();
}

export function insert(object: string, row: Record<string, any>) {
  /* A REAL uuid, because every primary key this queue writes to is one.

     This used to mint `new-<timestamp>-<random>`, which reads well in a local overlay and is
     rejected outright by Postgres. The sync layer now converts a legacy identifier of that
     shape deterministically, so records already sitting in someone's queue are recoverable,
     but anything created from here on should simply be valid to begin with. */
  const id = row.id
    || (typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `new-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`);
  overlay[object] = overlay[object] || {};
  overlay[object][id] = { ...row, id, _new: true };
  saveOverlay();
  enqueue('insert', object, id, { ...row, id });
  emit();
  void syncQueue();
  return id;
}

/* --- Global search -------------------------------------------------------------------
   One function over every object type, so the command palette does not need to know what
   exists. Adding an object to OBJECTS makes it searchable with no other change. */

export interface SearchHit {
  object: string;
  id: string;
  title: string;
  subtitle?: string;
  route: string;
  score: number;
}

export function subsequenceScore(needle: string, haystack: string): number | null {
  if (!needle) return 0;
  const n = needle.toLowerCase();
  const h = haystack.toLowerCase();
  const direct = h.indexOf(n);
  if (direct === 0) return 1000;
  if (direct > 0) return 700 - direct;
  let i = 0, score = 0, streak = 0;
  for (let j = 0; j < h.length && i < n.length; j++) {
    if (h[j] === n[i]) {
      streak += 1;
      score += streak * 2 + (j === 0 || h[j - 1] === ' ' ? 6 : 0);
      i += 1;
    } else streak = 0;
  }
  return i === n.length ? score : null;
}

export function search(q: string, limit = 24): SearchHit[] {
  if (!q.trim()) return [];
  const out: SearchHit[] = [];
  for (const def of Object.values(OBJECTS)) {
    for (const row of list<any>(def.key)) {
      const title = def.titleOf(row) ?? '';
      const subtitle = def.subtitleOf?.(row) ?? '';
      const s = Math.max(
        subsequenceScore(q, title) ?? -1,
        (subsequenceScore(q, subtitle) ?? -1) - 200,
      );
      if (s >= 0) {
        out.push({
          object: def.key, id: String(row.id ?? title), title, subtitle,
          route: def.route(String(row.id ?? '')), score: s,
        });
      }
    }
  }
  return out.sort((a, b) => b.score - a.score).slice(0, limit);
}

/* --- Diagnostics for the status bar ---------------------------------------------------- */

export function backendStatus() {
  return {
    backend: BACKEND,
    online: typeof navigator === 'undefined' ? true : navigator.onLine,
    pending: pendingWrites().length,
    label:
      BACKEND === 'supabase'
        ? 'Supabase'
        : 'Local seed data. Set VITE_SUPABASE_URL to connect Postgres.',
  };
}

/** Clears session writes. Used by the status bar; never touches the seed. */
export function resetOverlay() {
  overlay = {};
  queue = [];
  saveOverlay();
  saveQueue();
  emit();
}
